import React from 'react';
import { formatUrl } from '@eventbrite/legacy-infra.http';
import { hasTicketsFromExternalProvider } from '../../transforms';
import { useEventCardTrackingContext } from '../EventCardTracking';
import type { EventCardLinkPropGetterConfig, EventCardLinkProps } from '../../types';
import { getEventCardLinkClickHandlers, getLinkTargetRel } from './utils';
import styles from './EventCardLink.module.scss';
import { defineMessage, useIntl } from 'react-intl';

export const getEventCardLinkProps = ({
    affCode: aff,
    className,
    label,
    statsigLocationString,
    onClick,
    isTargetBlank = true,
    event: { ticketsBy, ticketsUrl, url, name, isPromoted },
}: EventCardLinkPropGetterConfig): EventCardLinkProps => {
    const isExternalProvider = hasTicketsFromExternalProvider({ ticketsBy });
    const formattedListingsUrl = formatUrl(url, {
        aff,
    });
    const formattedUrl =
        isExternalProvider && isPromoted && ticketsUrl ? ticketsUrl : formattedListingsUrl;

    const { target, rel } = getLinkTargetRel({
        url: formattedUrl,
        isTargetBlank,
        isNoFollow: isExternalProvider,
    });
    const clickHandlers = getEventCardLinkClickHandlers({
        statsigLocationString,
        onClick,
    });

    return {
        href: formattedUrl,
        rel,
        target,
        clickHandlers: clickHandlers,
        className,
        'aria-label':
            label || defineMessage({ id: 'View {eventName}', defaultMessage: 'View {eventName}' }),
    };
};

export const EventCardLinkBase: React.FunctionComponent<EventCardLinkProps> = ({
    children,
    ...props
}) => {
    const { clickHandlers, 'aria-label': ariaLabel, ...remainingProps } = props;
    const { formatMessage } = useIntl();
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        clickHandlers.forEach((fn) => fn(e));
    };

    const { heapDataAttributes } = useEventCardTrackingContext();

    return (
        <a
            {...remainingProps}
            aria-label={typeof ariaLabel === 'string' ? ariaLabel : formatMessage(ariaLabel)}
            className={`${styles['event-card-link']} ${props.className || ''}`}
            onClick={(e) => handleClick(e)}
            data-event-id={heapDataAttributes?.eventId}
            data-event-location={heapDataAttributes?.locationSlug}
            data-event-paid-status={heapDataAttributes?.paidStatus}
            data-event-category={heapDataAttributes?.category}
        >
            {children}
        </a>
    );
};
